'use client';

import { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useSuspenseQuery } from '@apollo/client';

import { ListingsFilterValuesQuery } from '@/query';
import { buildSearchQuery, prepareBookingParams } from '@/utils';
import { useRouter } from '@/i18n/routing';
import { Modal } from '@/component/Modal';
import { Button } from '@/component/Form/Button';
import { Price } from './Price';
import { Counter } from './Counter';
import { Checkboxes } from './Checkboxes';

export const FiltersModal = ({ children, searchState, variables }) => {
  const t = useTranslations();
  const { push } = useRouter();
  const locale = useLocale();
  const { data } = useSuspenseQuery(ListingsFilterValuesQuery, {
    variables: {
      ...variables,
      locale,
    },
    errorPolicy: 'ignore',
  });

  const [state, setState] = useState(searchState);
  const [redirecting, setRedirecting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const toggleModal = () => setModalOpen(!modalOpen);

  const closeModal = () => {
    toggleModal();
    setState(searchState);
    setRedirecting(false);
  };

  const applyFilters = async () => {
    const params = prepareBookingParams(state);
    const query = {
      ...params,
      date: params.dateRange,
      inexactStay: params.dateFlexibility,
    };

    setRedirecting(true);
    await push(
      `/listings${
        searchState?.tab ? `/filters/${searchState.tab}` : ''
      }${buildSearchQuery(query)}`,
      undefined,
      { shallow: true }
    );
    setRedirecting(false);
    toggleModal();
  };

  const clearFilters = () => {
    setState((state) => ({
      ...state,
      rooms: {},
      price: { min: null, max: null },
      amenities: [],
    }));
  };

  const chosenRatingClass =
    '[&&]:bg-primary-800 [&&]:border-primary-800 [&&]:text-white';

  return (
    <>
      <Modal
        show={modalOpen}
        close={closeModal}
        header={
          <>
            <Button
              className="max-sm:order-2 sm:min-w-[130px]"
              theme="text"
              size="md"
              icon="icon-clear"
              hideTextSm
              iconLeft
              onClick={clearFilters}
              dataTestId="filters-reset-button"
            >
              {t('listings.filters.header.clear-all')}
            </Button>
            <span className="max-sm:ml-[18px] sm:-ml-[86px] w-full max-sm:text-base max-sm:font-medium text-center text-slate-700 sm:text-2xl sm:font-semibold sm:leading-[30px] sm:tracking-tight">
              {t('listings.filters.header.title')}
            </span>
          </>
        }
        footer={
          <>
            <Button
              className="sm:hidden"
              theme="text"
              size="md"
              onClick={closeModal}
            >
              {t('listings.filters.footer.close')}
            </Button>
            <Button
              onClick={applyFilters}
              icon="icon-done_round"
              iconLeft
              size="md"
              disabled={redirecting}
              dataTestId="filters-apply-button"
            >
              {t('listings.filters.footer.apply')}
            </Button>
          </>
        }
        footerClass="sm:justify-end"
        mobileTopHeader
      >
        <div>
          <div className="flex flex-col gap-1">
            <b className="text-primary-800 text-2xl font-semibold leading-[30px] tracking-[0.2px]">
              {t('listings.filters.prices.range')}
            </b>
            <small className="text-slate-500 text-lg font-normal leading-7">
              {t('listings.filters.prices.sub')}
            </small>
            <Price
              value={state.price}
              min={data?.listingsFilterValues?.price?.min}
              max={data?.listingsFilterValues?.price?.max}
              onChange={(value) =>
                setState((prev) => ({ ...prev, price: value }))
              }
            />
          </div>

          <div className="flex flex-col gap-8 py-8 border-t border-b border-slate-200">
            <div className="">
              <b className="text-primary-800 text-2xl font-semibold leading-[30px] tracking-[0.2px]">
                {t('listings.filters.rating.title')}
              </b>
            </div>
            <div className="flex max-sm:flex-col gap-3">
              <Button
                onClick={() => setState((prev) => ({ ...prev, rating: null }))}
                theme="chip"
                size="sm"
                disabled={redirecting}
                className={`${state.rating === null ? chosenRatingClass : ''}`}
              >
                {t('listings.filters.rating.any')}
              </Button>
              <Button
                onClick={() => setState((prev) => ({ ...prev, rating: 5 }))}
                theme="chip"
                size="sm"
                disabled={redirecting}
                className={`${state.rating === 5 ? chosenRatingClass : ''}`}
              >
                {t('listings.filters.rating.5')}
              </Button>
              <Button
                onClick={() => setState((prev) => ({ ...prev, rating: 4 }))}
                theme="chip"
                size="sm"
                disabled={redirecting}
                className={`${state.rating === 4 ? chosenRatingClass : ''}`}
              >
                {t('listings.filters.rating.4')}
              </Button>
              <Button
                onClick={() => setState((prev) => ({ ...prev, rating: 3 }))}
                theme="chip"
                size="sm"
                disabled={redirecting}
                className={`${state.rating === 3 ? chosenRatingClass : ''}`}
              >
                {t('listings.filters.rating.3')}
              </Button>
              <Button
                onClick={() => setState((prev) => ({ ...prev, rating: 0 }))}
                theme="chip"
                size="sm"
                disabled={redirecting}
                className={`${state.rating === 0 ? chosenRatingClass : ''}`}
              >
                {t('listings.filters.rating.unrated')}
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-8 py-8 border-t border-b border-slate-200">
            <div className="">
              <b className="text-primary-800 text-2xl font-semibold leading-[30px] tracking-[0.2px]">
                {t('listings.filters.rooms.title')}
              </b>
            </div>
            <div className="flex flex-col gap-4">
              <Counter
                title={t('listings.filters.rooms.bedrooms')}
                value={state?.rooms?.bedrooms || 0}
                onChange={(value) =>
                  setState((prev) => ({
                    ...prev,
                    rooms: { ...prev.rooms, bedrooms: value },
                  }))
                }
                dataTestid="bedrooms-counter"
              />
              <Counter
                title={t('listings.filters.rooms.beds')}
                value={state?.rooms?.beds || 0}
                onChange={(value) =>
                  setState((prev) => ({
                    ...prev,
                    rooms: { ...prev.rooms, beds: value },
                  }))
                }
                dataTestid="beds-counter"
              />
              <Counter
                title={t('listings.filters.rooms.bathrooms')}
                value={state?.rooms?.bathrooms || 0}
                onChange={(value) =>
                  setState((prev) => ({
                    ...prev,
                    rooms: { ...prev.rooms, bathrooms: value },
                  }))
                }
                dataTestid="bathrooms-counter"
              />
            </div>
          </div>
          <div>
            <b className="block py-8 text-primary-800 text-2xl font-semibold leading-[30px] tracking-[0.2px]">
              {t('listings.filters.amenities.title')}
            </b>
            <Checkboxes
              value={state.amenities}
              options={data?.listingsFilterValues?.amenities}
              onChange={(value) =>
                setState((prev) => ({ ...prev, amenities: value }))
              }
              dataTestid="amenities-checkboxes"
            />
          </div>
        </div>
      </Modal>
      {children({ openModal: toggleModal })}
    </>
  );
};
